import React, { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import Layout from "../../../Components/Layout";
import NameCell from "../../../Components/ProGrid/components/NameCell";
import ProGrid from "../../../Components/ProGrid/v2";
import DialogModal from "../../../Components/DialogModel/index.js";
import { useNavigate } from "react-router-dom";
import { STAUTES } from "../../../../utils/constant";
import { DELETE_JOB_TITLE, GET_ALL_JOB_TITLE, DUPLICATE_JOB_TITLES, ACTIVATE_JOB_TITLE } from "../../../../utils/services/apiPath";
import { deleteApi, postApi, getApi, putApi } from "../../../../utils/services";
import { errorToast, successToast } from "../../../../utils/toastHelper";
import usePageState from "../../../../utils/customHooks/usePageState";
import { subModPermissionSelector } from "../../../store/features/permissions/permissionsSlice";
import usePermission from "../../../../utils/customHooks/usePermission";
import { listLaborClassificationTitleFormName } from "../../../../utils/formHelper";
import { R_LABOR_CLASSIFICATION_TITLE, R_SETTINGS } from "../../../../utils/permissionReferenceConfig";
import useColumn from "../../../../utils/customHooks/useColumns";
import ProToggleField from "../../../Components/Inputs/ToggleButton";
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from '@mui/material';

const getRedirectUrl = params => {
  return `/settings/labor-classification-title/${params.id}`;
};

export default function JobTitleList() {
  const navigation = useNavigate();
  const {
    currentPage,
    pageSize,
    data: jobTitleData,
    rowCount,
    status,
    sortModel,
    searchString,
    setPageState,
    setPagination,
    setStatus,
    setSortModel,
    setSearchString
  } = usePageState();
  const { [listLaborClassificationTitleFormName]: listPermission } = usePermission(subModPermissionSelector(R_SETTINGS, R_LABOR_CLASSIFICATION_TITLE), listLaborClassificationTitleFormName);
  const { canView, canAdd, canExport, canDelete } = listPermission;
  const [duplicateJobTitles, setDuplicateJobTitles] = useState({});
  const [isDuplicateJobTitles, setIsDuplicateJobTitles] = useState(false);
  const [isDeleteError, setIsDeleteError] = useState(false);
  const [deleteErrorMessage, setDeleteErrorMessage] = useState('');
  const [toggleStates, setToggleStates] = useState({});
  const [pendingToggle, setPendingToggle] = useState(null);
  const [confirmLabourClassStatus, setConfirmLabourClassStatus] = useState({
    open: false,
    params: null,
    newStatus: null,
    labourClassification: '',
    statusText: ''
  });
  const ToggleCell = ({ params, toggleStates, handleStatusToggle }) => {
    const currentToggleState = params.row.isActive;
    const toggleForm = useForm({
      defaultValues: { isActive: currentToggleState }
    });

    useEffect(() => {
      toggleForm.setValue("isActive", currentToggleState);
    }, [currentToggleState, toggleForm]);
    
    return (
      <ProToggleField
        name="isActive"
        control={toggleForm.control}
        label=""
        rules={{}}
        mode="edit"
        permission={{
          canView: true,
          canEdit: true
        }}
        onChange={(event, newValue) => {
          toggleForm.setValue("isActive", currentToggleState);
          handleStatusToggle(params, newValue);
        }}
        size="small"
      />
    );
  };
    
  const handleStatusToggle = async (params, newStatus) => {
    const statusText = newStatus ? 'Active' : 'Inactive';
    setPendingToggle({
      jobTitleId: params.row.jobTitleId,
      newStatus: newStatus
    });
    setConfirmLabourClassStatus({
      open: true,
      params,
      newStatus,
      labourClassification: params.row.jobTitleName,
      statusText
    });
  };
    
  const handleYesContinue = async () => {
    const { params, newStatus } = confirmLabourClassStatus;
    const jobTitleId = params.id
    try {
      if(newStatus) {
        const { error } = await putApi(`${ACTIVATE_JOB_TITLE}/${jobTitleId}/activate`);
        if (error) {
          errorToast(error);
          return;
        }
        getJobTitles();
        successToast(`Job Title status updated to Active`);
      } else {
        onDelete(jobTitleId);
      }
    } catch (err) {
      errorToast('Failed to update Job Title status');
    } finally {
      setConfirmLabourClassStatus({ open: false, params: null, newStatus: null, labourClassification: '', statusText: '' });
    }
  };
  
  const handleNoCancel = () => {
    setConfirmLabourClassStatus({ open: false, params: null, newStatus: null, labourClassification: '', statusText: '' });
  };

  const columns = useColumn(
    [
      {
        field: "jobTitleName",
        headerName: "Job Title",
        renderCell: params => <NameCell params={params} getRedirectUrl={getRedirectUrl} />
      },
      {
        field: "jobCategoryName",
        headerName: "Job Category"
      },
      {
        field: "jobTitleDescription",
        headerName: "Description"
      },
      {
        field: "isActive",
        headerName: "Active",
        width: 120,
        renderCell: params => (
          <ToggleCell 
            params={params}
            toggleStates={toggleStates}
            handleStatusToggle={handleStatusToggle}
          />
        )
      }
    ],
    canDelete
  );

  const onDelete = async id => {
    const { error } = await deleteApi(DELETE_JOB_TITLE, [{ jobTitleId: id }]);
    if (error){
      setDeleteErrorMessage(error);
      setIsDeleteError(true);
    }else {
      getJobTitles();
      successToast(`Job Title status updated to Inactive`);
    }
  };

  const addButtonAction = () => {
    navigation("/settings/labor-classification-title/add");
  };

  const getJobTitles = useCallback(async () => {
    setStatus(STAUTES.LOADING);
    const payload = {
      pageIndex: searchString ? 1 : currentPage + 1,
      pageSize: pageSize,
      searchString: searchString,
      orderByAscending: true,
      orderCol: "jobTitleName",
      ...sortModel
    };
    const { data, totalRecords, error } = await postApi(GET_ALL_JOB_TITLE, payload);
    setStatus(STAUTES.IDLE);
    if (error) return errorToast(error);
    setPageState(prevPageInfo => ({ ...prevPageInfo, data: data || [], rowCount: totalRecords }));
  }, [currentPage, pageSize, sortModel, searchString, setPageState, setStatus]);

  const CheckDuplicateJobTitles = async () => {
    const data = await getApi(DUPLICATE_JOB_TITLES);
    setDuplicateJobTitles(data);
    setIsDuplicateJobTitles(data?.hasDuplicates || false);
  };

  const handleDuplicateJobTitlesPopup = () => {
    setIsDuplicateJobTitles(false);
  };

  const handleDeleteErrorPopup = () => {
    setIsDeleteError(false);
  };

  useEffect(() => {
    getJobTitles();
  }, [getJobTitles]);

  useEffect(() => {
    CheckDuplicateJobTitles();
  }, []);

  return (
    <Layout canView={canView}>
      <ProGrid
        title="Job Title"
        columns={columns}
        loading={status === STAUTES.LOADING}
        rows={jobTitleData}
        searchMode={!!searchString}
        searchModel={{ defaultValue: searchString, handleDebounce: setSearchString }}
        addButtonLabel="+ Add Job Title"
        addButtonAction={addButtonAction}
        hideAddButton={!canAdd}
        hideExport={!canExport}
        options={{
          getRowId: row => row.jobTitleId,
          rowCount: rowCount,
          paginationMode: "server",
          paginationModel: { pageSize: pageSize, page: currentPage },
          onPaginationModelChange: setPagination,
          sortingMode: "server",
          onSortModelChange: setSortModel
        }}
      />
      <DialogModal
        show={isDuplicateJobTitles}
        title="Alert"
        message={duplicateJobTitles?.message}
        onConfirm={handleDuplicateJobTitlesPopup}
        showCancel={false}
        confirmBtnText={"Okay"}
      />
      <DialogModal
        show={isDeleteError}
        title="Alert"
        message={deleteErrorMessage}
        onConfirm={handleDeleteErrorPopup}
        showCancel={false}
        confirmBtnText={"Okay"}
      />
      <Dialog
        open={confirmLabourClassStatus.open}
        onClose={handleNoCancel}
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-description"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="confirm-dialog-title">
          Confirm Status Change
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-dialog-description">
            Are you sure you want to change {confirmLabourClassStatus.labourClassification}'s status to {confirmLabourClassStatus.statusText}?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px' }}>
          <Button 
            onClick={handleNoCancel} 
            variant="outlined"
            color="primary"
            sx={{ minWidth: '120px' }}
          >
            No, Cancel
          </Button>
          <Button 
            onClick={handleYesContinue} 
            variant="contained"
            color="primary"
            autoFocus
            sx={{ minWidth: '120px' }}
          >
            Yes, Continue
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
}
